import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDatePicker } from "@/components/CalendarDatePicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  phone: "",
  email: "",
  vehicle_type: "",
  service_interest: "",
  service_address: "",
  vehicle_year: "",
  vehicle_make: "",
  vehicle_model: "",
  photo_name: "",
  photo_data_url: "",
  requested_appointment_date: "",
  message: "",
};

export const QuoteForm = () => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 2_000_000) {
      toast.error("Please upload an image under 2MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((current) => ({ ...current, photo_name: file.name, photo_data_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.vehicle_type || !form.service_interest) {
      toast.error("Please add your name, phone, vehicle type, and service.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/quote-requests`, form);
      toast.success("Request received. A.J. Auto & RV Care can follow up with a quote.");
      setForm(initialForm);
    } catch (error) {
      toast.error("Something went wrong. Please call or text instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-6 shadow-2xl shadow-black/40 backdrop-blur-xl md:p-8" data-testid="quote-request-form">
      <div className="grid gap-4 sm:grid-cols-2" data-testid="quote-form-fields">
        <Input data-testid="quote-name-input" value={form.name} onChange={(event) => updateField("name", event.target.value)} placeholder="Your name" className="h-12 rounded-2xl border-white/10 bg-white/[0.05] text-white placeholder:text-zinc-500 focus-visible:ring-blue-400" />
        <Input data-testid="quote-phone-input" value={form.phone} onChange={(event) => updateField("phone", event.target.value)} placeholder="Phone number" className="h-12 rounded-2xl border-white/10 bg-white/[0.05] text-white placeholder:text-zinc-500 focus-visible:ring-blue-400" />
        <Input data-testid="quote-email-input" type="email" value={form.email} onChange={(event) => updateField("email", event.target.value)} placeholder="Email optional" className="h-12 rounded-2xl border-white/10 bg-white/[0.05] text-white placeholder:text-zinc-500 focus-visible:ring-blue-400" />
        <Input data-testid="quote-address-input" value={form.service_address} onChange={(event) => updateField("service_address", event.target.value)} placeholder="Mobile service address optional" className="h-12 rounded-2xl border-white/10 bg-white/[0.05] text-white placeholder:text-zinc-500 focus-visible:ring-blue-400" />
        <Select value={form.vehicle_type} onValueChange={(value) => updateField("vehicle_type", value)}>
          <SelectTrigger data-testid="quote-vehicle-select" className="h-12 rounded-2xl border-white/10 bg-white/[0.05] text-white focus:ring-blue-400">
            <SelectValue placeholder="Vehicle type" />
          </SelectTrigger>
          <SelectContent data-testid="quote-vehicle-options" className="border-white/10 bg-zinc-950 text-white">
            <SelectItem value="Small" data-testid="vehicle-small-option">Small</SelectItem>
            <SelectItem value="Medium" data-testid="vehicle-medium-option">Medium</SelectItem>
            <SelectItem value="Large" data-testid="vehicle-large-option">Large</SelectItem>
            <SelectItem value="XL" data-testid="vehicle-xl-option">XL</SelectItem>
            <SelectItem value="RV and Camper" data-testid="vehicle-rv-option">RV and Camper</SelectItem>
          </SelectContent>
        </Select>
        <Input data-testid="quote-year-input" value={form.vehicle_year} onChange={(event) => updateField("vehicle_year", event.target.value)} placeholder="Year optional" className="h-12 rounded-2xl border-white/10 bg-white/[0.05] text-white placeholder:text-zinc-500 focus-visible:ring-blue-400" />
        <Input data-testid="quote-make-input" value={form.vehicle_make} onChange={(event) => updateField("vehicle_make", event.target.value)} placeholder="Make optional" className="h-12 rounded-2xl border-white/10 bg-white/[0.05] text-white placeholder:text-zinc-500 focus-visible:ring-blue-400" />
        <Input data-testid="quote-model-input" value={form.vehicle_model} onChange={(event) => updateField("vehicle_model", event.target.value)} placeholder="Model optional" className="h-12 rounded-2xl border-white/10 bg-white/[0.05] text-white placeholder:text-zinc-500 focus-visible:ring-blue-400" />
        <CalendarDatePicker value={form.requested_appointment_date} onChange={(dateValue) => updateField("requested_appointment_date", dateValue)} availabilityDays={[]} label="Requested appointment date optional" testId="quote-requested-date-picker" />
        <div className="sm:col-span-2" data-testid="quote-service-select-wrapper">
          <Select value={form.service_interest} onValueChange={(value) => updateField("service_interest", value)}>
            <SelectTrigger data-testid="quote-service-select" className="h-12 rounded-2xl border-white/10 bg-white/[0.05] text-white focus:ring-blue-400">
              <SelectValue placeholder="Service interested in" />
            </SelectTrigger>
            <SelectContent data-testid="quote-service-options" className="border-white/10 bg-zinc-950 text-white">
              <SelectItem value="Interior detailing" data-testid="service-interior-option">Interior detailing</SelectItem>
              <SelectItem value="Exterior cleaning" data-testid="service-exterior-option">Exterior cleaning</SelectItem>
              <SelectItem value="RV care" data-testid="service-rv-option">RV care</SelectItem>
              <SelectItem value="Tire rotation" data-testid="service-tire-option">Tire rotation</SelectItem>
              <SelectItem value="Oil change" data-testid="service-oil-option">Oil change</SelectItem>
              <SelectItem value="Code scan / minor diagnostics" data-testid="service-diagnostic-option">Code scan / minor diagnostics</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.05] p-4 sm:col-span-2" data-testid="quote-photo-upload-wrapper">
          <label className="block text-sm font-medium text-white" htmlFor="quote-photo-upload" data-testid="quote-photo-upload-label">Add a photo of your vehicle optional</label>
          <Input id="quote-photo-upload" data-testid="quote-photo-upload-input" type="file" accept="image/*" onChange={handlePhotoChange} className="mt-3 h-12 rounded-2xl border-white/10 bg-black/20 text-white file:mr-4 file:rounded-full file:border-0 file:bg-blue-500 file:px-4 file:py-2 file:text-white" />
          {form.photo_name && <p className="mt-2 text-xs text-blue-200" data-testid="quote-photo-name">Photo added: {form.photo_name}</p>}
        </div>
        <Textarea data-testid="quote-message-textarea" value={form.message} onChange={(event) => updateField("message", event.target.value)} placeholder="Tell us vehicle condition, RV length, or preferred service details." className="min-h-32 rounded-2xl border-white/10 bg-white/[0.05] text-white placeholder:text-zinc-500 focus-visible:ring-blue-400 sm:col-span-2" />
      </div>
      <Button disabled={loading} className="mt-6 h-12 w-full rounded-full bg-blue-500 text-white shadow-[0_0_30px_rgba(59,130,246,0.25)] transition-transform hover:-translate-y-0.5 hover:bg-blue-400 active:scale-95" data-testid="quote-submit-button">
        {loading ? "Sending request..." : "Request quote"}
      </Button>
      <p className="mt-4 text-xs leading-relaxed text-zinc-500" data-testid="quote-form-note">Submitting this form stores your request so A.J. Auto & RV Care can respond with availability and exact pricing.</p>
    </form>
  );
};